import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  PdfPen,
  PdfColor,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import { render, BookOptions } from '../../pdf/book';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);

export class TestBookAlignment implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    const markdown = `
# Paragraph Alignment Test

This document demonstrates various paragraph alignment options available in the book typesetting system.

## Left Alignment (Default)

This is a paragraph with default left alignment. The text starts from the left margin and extends to the right. This is the standard alignment for most documents and is the default behavior when no alignment is specified. This paragraph contains enough text to wrap across multiple lines to demonstrate the alignment effect.

## Center Alignment

::: center
This paragraph uses center alignment. The text is centered horizontally within the available line width. Center alignment is commonly used for titles, quotes, or special text elements that need visual emphasis. This paragraph also contains multiple lines to show how center alignment works across the entire paragraph.
:::

## Right Alignment

::: right
This paragraph uses right alignment. The text is aligned to the right margin, creating a flush-right appearance. This alignment style is useful for dates, signatures, or when placing text on the opposite side of the page from other content. The lines wrap and stay aligned to the right edge.
:::

## Left Alignment with Explicit Tag

::: left
This paragraph explicitly uses left alignment. Even though left is the default, using the explicit tag ensures the alignment is set as intended. This can be useful for code or when you want to be explicit about the formatting choice. The text flows naturally from left to right.
:::

## Mixed Alignment

Here is a normal paragraph that demonstrates how different alignment styles can be mixed in a single document. The book renderer handles each alignment block independently while maintaining consistent typography throughout.

::: center
**Centered Bold Text**

This is a centered paragraph that can contain **bold**, *italic*, and \`inline code\` formatting. The alignment applies to the entire paragraph while preserving all inline styles.
:::

After the centered paragraph, we return to normal left-aligned text.

## Short Centered Paragraph

::: center
Centered short text
:::

## Another Short Right-Aligned Paragraph

::: right
Right aligned short text
:::

## Long Justified Paragraph

This is a very long paragraph that demonstrates justified text alignment in the book typesetting system. Justified text is text that is aligned on both the left and right margins, creating a clean block-like appearance. This is the standard formatting used in newspapers, books, and most printed publications. The text is stretched to fill the entire line width, with spacing adjusted between words to achieve uniform line lengths. This paragraph contains enough text to span multiple lines and even multiple paragraphs to demonstrate how the justification works throughout a larger section of text. The algorithm distributes extra space evenly between all words on each line, ensuring that each line reaches exactly to the right margin while maintaining readable word spacing. This creates the characteristic clean edges that justified text is known for, though it can sometimes create awkward gaps in lines with very long words or narrow columns.

Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.

Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?

At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident, similique sunt in culpa qui officia deserunt mollitia animi, id est laborum et dolorum fuga. Et harum quidem rerum facilis est et expedita distinctio. Nam libero tempore, cum soluta nobis est eligendi optio cumque nihil impedit quo minus id quod maxime placeat facere possimus, omnis voluptas assumenda est, omnis dolor repellendus.

# Summary

The alignment system supports:
- **Left** - Text aligned to the left margin
- **Center** - Text centered horizontally  
- **Right** - Text aligned to the right margin
- **Justify** (default) - Text aligned to both margins

Use the \`:::\` fence syntax to create alignment blocks:
- \`\`\`markdown
::: center
Your centered text here
:::\`\`\`
`;

    const options: BookOptions = {
      fontSize: 11,
      lineHeight: 1.5,
      margin: 20,
      columns: 1,
      defaultAlignment: 'justify',
    };

    await render(pdf, markdown, options);

    // Draw layout rectangle bounds for debugging
    const margin = 20;
    const pageWidth = PdfSize.A4.width;
    const pageHeight = PdfSize.A4.height;
    const contentWidth = pageWidth - 2 * margin;
    const contentHeight = pageHeight - 2 * margin;

    const rectPen = new PdfPen(PdfColor.fromRgb(255, 0, 0), 0.5);

    for (const page of pdf.gs) {
      const contentRect = new PdfRect(margin, margin, contentWidth, contentHeight);
      page.drawRectangle(rectPen, null, contentRect);
    }
  }
}
